import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildTooltipConfig } from '../../utils/chart.utils';
import { getDefaultColor, getContrastingTextColor } from '../../utils/color.utils';

type TimelineMode = 'date' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

interface TaskRecord {
  [key: string]: unknown;
}

interface TaskItem {
  name: string;
  rowLabel: string;
  selectionKey: string;
  dataLabel: string;
  colorKey: string;
  group: string;
  status: string;
  owner: string;
  start: number;
  end: number;
  progress: number | null;
  color: string;
}

interface LegendItem {
  label: string;
  color: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-gantt-chart',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe, CapitalCasePipe],
  templateUrl: './gantt-chart.component.html',
  styleUrls: ['./gantt-chart.component.scss'],
})
export class GanttChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  @ViewChild('chartHost', { static: true }) chartHost!: ElementRef<HTMLDivElement>;

  protected myChart!: echarts.ECharts;
  protected allSeriesNames: string[] = [];
  protected allCategories: string[] = [];
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  protected hasError = false;
  protected errorMessage = '';
  protected resolvedHeight = '420px';
  protected resolvedCardHeight = 'calc(420px + 120px)';
  protected timelineLabel = 'Monthly Timeline';
  protected rangeLabel = '';
  protected taskCount = 0;
  protected legendItems: LegendItem[] = [];
  protected selectedLegendLabels: string[] = [];

  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastSource?: unknown;
  private allTasks: TaskItem[] = [];
  private visibleTasks: TaskItem[] = [];
  private currentMode: TimelineMode = 'monthly';
  private currentDomain: { min: number; max: number } | null = null;

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => this.allSeriesNames,
      getCategories: () => this.allCategories,
      onHighlightCategories: (categories) => this.applyCategoryHighlight(categories),
      onResetCategoryHighlight: () => this.resetCategoryHighlight(),
    });

    this.loadAndRenderChart(initialFilters ?? undefined);
     this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      // Recompute height from updated --dvm-chart-height-lg CSS variable,
      // then resize the chart so it fills the new vertical space.
      this.refreshResponsiveLayout();
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (this.lastSource === undefined || !this.myChart || !this.chartHost?.nativeElement) return;
      this.myChart.dispose();
      this.myChart = echarts.init(this.chartHost.nativeElement, 'dvm');
      this.renderChart(this.lastSource);
    });
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.myChart?.dispose();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.myChart?.resize();
  }

  protected loadAndRenderChart(filters?: any): void {
    if (!this.myChart) {
      this.myChart = echarts.init(this.chartHost.nativeElement, 'dvm');
    }

    this.myChart.showLoading();

    if (this.data?.apiUrl) {
      this.configService.getConfig(
        this.data.apiUrl,
        this.data,
        {
          ...(filters || {}),
          useQueryParams: this.data?.useQueryParams || this.data?.useQueryParam || false,
        },
        this.data?.method,
      )?.subscribe({
        next: (response) => this.renderChart(response),
        error: () => this.showError('Unable to load roadmap timeline data.'),
      });
      return;
    }

    this.renderChart(this.data?.data ?? this.data);
  }

  protected renderChart(source: unknown): void {
    this.lastSource = source;
    const tasks = this.normalizeTasks(this.extractRows(source));
    if (!tasks.length) {
      this.showError('No roadmap items available for the gantt chart.');
      return;
    }

    this.allTasks = tasks;
    this.currentMode = this.resolveTimelineMode();
    this.currentDomain = this.resolveDomain(tasks, this.currentMode);
    this.legendItems = this.resolveLegendItems(tasks);
    this.allSeriesNames = this.legendItems.map((item) => item.label);
    this.selectedLegendLabels = this.selectedLegendLabels.filter((label) =>
      this.legendItems.some((item) => item.label === label),
    );
    this.timelineLabel = this.getTimelineLabel(this.currentMode);
    this.rangeLabel = `${this.formatDate(this.currentDomain.min)} to ${this.formatDate(this.currentDomain.max)}`;
    this.hasError = false;
    this.errorMessage = '';

    this.renderVisibleTasks();
  }

  protected showError(message: string): void {
    this.hasError = true;
    this.errorMessage = message;
    this.rangeLabel = '';
    this.taskCount = 0;
    this.legendItems = [];
    this.selectedLegendLabels = [];
    this.allCategories = [];
    this.allTasks = [];
    this.visibleTasks = [];
    this.myChart?.clear();
    this.myChart?.hideLoading();
  }

  protected extractRows(source: unknown): TaskRecord[] {
    if (Array.isArray(source)) {
      return source.filter((item): item is TaskRecord => !!item && typeof item === 'object');
    }
    if (!source || typeof source !== 'object') {
      return [];
    }

    const record = source as Record<string, unknown>;
    const taskPath = this.data?.chartConfig?.taskPath || this.data?.taskPath || this.data?.dataPath;
    const directCandidates = [
      typeof taskPath === 'string' && taskPath ? this.byPath(record, taskPath) : undefined,
      record['tasks'],
      record['items'],
      record['rows'],
      this.byPath(record, 'roadmap.tasks'),
      this.byPath(record, 'data.tasks'),
      this.byPath(record, 'data.data.tasks'),
      record['data'],
    ];

    for (const candidate of directCandidates) {
      if (Array.isArray(candidate)) {
        return candidate.filter((item): item is TaskRecord => !!item && typeof item === 'object');
      }
    }

    const groupedRows = this.extractRowsFromCategories(record);
    if (groupedRows.length) {
      return groupedRows;
    }

    const nestedData = record['data'];
    if (nestedData && typeof nestedData === 'object') {
      return this.extractRows(nestedData);
    }

    return [];
  }

  protected extractRowsFromCategories(record: Record<string, unknown>): TaskRecord[] {
    const categoryPath = this.data?.chartConfig?.categoryPath || this.data?.categoryPath;
    const categoryCandidates = [
      typeof categoryPath === 'string' && categoryPath ? this.byPath(record, categoryPath) : undefined,
      record['categories'],
      record['groups'],
      record['tracks'],
      this.byPath(record, 'roadmap.categories'),
      this.byPath(record, 'data.categories'),
      this.byPath(record, 'data.data.categories'),
    ];

    for (const candidate of categoryCandidates) {
      if (!Array.isArray(candidate)) {
        continue;
      }

      const rows = candidate.flatMap((category) => this.rowsFromCategory(category));
      if (rows.length) {
        return rows;
      }
    }

    return [];
  }

  protected rowsFromCategory(category: unknown): TaskRecord[] {
    if (!category || typeof category !== 'object') {
      return [];
    }

    const categoryRecord = category as TaskRecord;
    const taskPath = this.data?.chartConfig?.categoryTaskPath || this.data?.categoryTaskPath || this.data?.taskPath;
    const taskCandidates = [
      typeof taskPath === 'string' && taskPath ? this.byPath(categoryRecord, taskPath) : undefined,
      categoryRecord['tasks'],
      categoryRecord['items'],
      categoryRecord['rows'],
      categoryRecord['children'],
    ];

    const groupName = this.readString(
      categoryRecord,
      this.data?.chartConfig?.categoryNameKey || this.data?.categoryNameKey,
      ['name', 'group', 'track', 'stream', 'category', 'label'],
    );

    for (const candidate of taskCandidates) {
      if (!Array.isArray(candidate)) {
        continue;
      }

      return candidate
        .filter((item): item is TaskRecord => !!item && typeof item === 'object')
        .map((item) => ({ __parentGroup: groupName, ...item }));
    }

    return [];
  }

  protected normalizeTasks(rows: TaskRecord[]): TaskItem[] {
    const colorMap = (this.data?.chartConfig?.colorMap || {}) as Record<string, string>;
    const colorBy = String(this.data?.chartConfig?.colorBy || 'status').trim();
    const colorIndex = new Map<string, number>();

    const tasks = rows
      .map((row) => {
        const name = this.readString(row, this.data?.taskNameKey, ['task', 'name', 'label', 'title']);
        const start = this.parseDate(
          this.readValue(row, this.data?.startKey, ['startDate', 'start', 'from']),
          false,
        );
        let end = this.parseDate(
          this.readValue(row, this.data?.endKey, ['endDate', 'end', 'to']),
          true,
        );

        if (!name || start === null) {
          return null;
        }

        if (end === null || end <= start) {
          end = start + DAY_MS;
        }

        const group = this.readString(row, this.data?.groupKey, [
          'group',
          'track',
          'stream',
          'category',
          '__parentGroup',
        ]);
        const status = this.readString(row, this.data?.statusKey, ['status', 'phase', 'state']);
        const owner = this.readString(row, this.data?.ownerKey, ['owner', 'team', 'assignee']);
        const progress = this.parseProgress(
          this.readValue(row, this.data?.progressKey, ['progress', 'completion']),
        );
        const dataLabel = this.resolveTaskBarLabel(row, { name, group, status, owner, progress });
        const explicitColor = this.readString(row, this.data?.colorKey, ['color', 'barColor']);
        const colorKey = this.resolveColorKey(row, colorBy, { name, group, status, owner });

        if (!colorIndex.has(colorKey)) {
          colorIndex.set(colorKey, colorIndex.size);
        }

        const defaultColor = getDefaultColor(colorIndex.get(colorKey) || 0);
        const resolvedColor =
          explicitColor ||
          this.resolveColorMapValue(colorMap, colorKey) ||
          defaultColor;

        return {
          name,
          rowLabel: this.buildRowLabel(name, group),
          selectionKey: this.buildSelectionKey(name, group),
          dataLabel,
          colorKey,
          group,
          status,
          owner,
          start,
          end,
          progress,
          color: resolvedColor,
        };
      })
      .filter((task): task is TaskItem => task !== null);

    if (this.data?.chartConfig?.sortTasks !== false) {
      tasks.sort((a, b) => a.start - b.start || a.end - b.end || a.rowLabel.localeCompare(b.rowLabel));
    }

    const uniqueRowLabels = this.uniquify(tasks.map((task) => task.rowLabel));
    const uniqueSelectionKeys = this.uniquify(tasks.map((task) => task.selectionKey));

    return tasks.map((task, index) => ({
      ...task,
      rowLabel: uniqueRowLabels[index],
      selectionKey: uniqueSelectionKeys[index],
    }));
  }

  protected buildOption(
    tasks: TaskItem[],
    mode: TimelineMode,
    domain: { min: number; max: number },
  ): echarts.EChartsOption {
    const left = this.resolveLeft(tasks);
    const showProgressLabel = this.data?.chartConfig?.showProgressLabel !== false;
    const useProgressTrack = this.data?.chartConfig?.showProgressTrack !== false;
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const tooltipConfig = buildTooltipConfig(this.data?.chartConfig?.tooltip);
    const taskLabelFontSize = this.resolveTaskLabelFontSize();
    const axisPosition = String(this.data?.chartConfig?.axisPosition || 'top').toLowerCase() === 'bottom'
      ? 'bottom'
      : 'top';
    const upperMode = axisPosition === 'top' ? this.resolveUpperTimelineMode(mode) : null;
    const responsiveUpperMode = this.resolveResponsiveUpperTimelineMode(upperMode);
    const hasUpperAxis = !!responsiveUpperMode;

    return {
      animationDuration: 400,
      grid: {
        left,
        right: 24,
        top: axisPosition === 'top' ? this.resolveTimelineGridPadding(axisPosition, hasUpperAxis) : 18,
        bottom: axisPosition === 'bottom' ? this.resolveTimelineGridPadding(axisPosition, hasUpperAxis) : 40,
        containLabel: false,
      },
      tooltip: {
        ...tooltipConfig,
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
      },
      xAxis: this.buildTimelineAxes(mode, domain, axisPosition, responsiveUpperMode),
      yAxis: {
        type: 'category',
        inverse: true,
        data: tasks.map((task) => task.rowLabel),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          width: left - 24,
          color: theme.chart?.textColor || '#0f172a',
          fontWeight: 500,
          fontSize: taskLabelFontSize,
          lineHeight: Math.round(taskLabelFontSize * 1.25),
          formatter: (value: string) => this.formatTaskAxisLabel(value, left - 24, taskLabelFontSize),
        },
        // Alternating row stripes — pull from the table tokens so dark themes
        // don't flash a bright white band between rows. Both bands derive
        // from theme tokens; the previous translucent slate first band
        // looked nearly white on dark themes whose card body is light-ish
        // (executive-dark, etc.), defeating the dark-mode goal.
        splitArea: {
          show: true,
          areaStyle: {
            color: [
              theme.table?.rowBg || theme.card?.bodyBg || 'rgba(148,163,184,0.08)',
              theme.table?.altRowBg || theme.card?.bodyBg || '#ffffff',
            ],
          },
        },
      },
      series: [
        {
          name: this.data?.seriesName || this.data?.title || 'Roadmap',
          type: 'custom',
          renderItem: (params: any, api: any) =>
            this.renderBar(params, api, showProgressLabel, useProgressTrack, theme),
          encode: { x: [1, 2], y: 0 },
          data: tasks.map((task, index) => ({
            name: task.selectionKey,
            value: [index, task.start, task.end, task.progress ?? -1, task.dataLabel],
            itemStyle: { color: task.color, opacity: 1 },
            taskName: task.name,
            task: task.name,
            category: task.group,
            group: task.group,
            status: task.status,
            owner: task.owner,
            start: task.start,
            end: task.end,
            startDate: this.formatDate(task.start),
            endDate: this.formatDate(task.end),
            duration: this.formatDuration(task.start, task.end),
            progress: task.progress,
            taskData: task,
          })),
        },
      ],
    };
  }

  protected renderBar(
    params: any,
    api: any,
    showProgressLabel: boolean,
    useProgressTrack: boolean,
    theme: any,
  ): echarts.CustomSeriesRenderItemReturn {
    const index = Number(api.value(0));
    const start = api.coord([api.value(1), index]);
    const end = api.coord([api.value(2), index]);
    const task = this.visibleTasks[index];
    const seriesStyle = api.style() as Record<string, unknown>;
    const baseColor = String(seriesStyle?.['fill'] || task?.color || getDefaultColor(index));
    const opacity = Number(seriesStyle?.['opacity'] ?? params?.data?.itemStyle?.opacity ?? 1);
    const barHeight = Math.max(18, Number(this.data?.chartConfig?.barHeight || 24));
    const progressLabelFontSize = this.resolveProgressLabelFontSize();
    // Bar outline that separates a filled segment from its surroundings —
    // keep it the surface color so it reads as a gap on every theme. The
    // previous '#ffffff' literal carved a bright halo around bars on dark
    // themes. Body bg fallback because the bar sits on the card body.
    const barStroke = String(theme?.card?.bodyBg || '#ffffff');
    // Low-progress text needs to be readable against the *unfilled* track,
    // which is the card body. Use the chart text color so dark themes get
    // a light label instead of the previous #0f172a (near-black on dark).
    const lowProgressTextColor = String(theme?.chart?.textColor || '#0f172a');
    const rect = echarts.graphic.clipRectByRect(
      {
        x: start[0],
        y: start[1] - barHeight / 2,
        width: Math.max(6, end[0] - start[0]),
        height: barHeight,
      },
      { x: params.coordSys.x, y: params.coordSys.y, width: params.coordSys.width, height: params.coordSys.height },
    );
    if (!rect) {
      return null;
    }

    const encodedProgress = Number(api.value(3));
    const progress = task?.progress ?? (Number.isFinite(encodedProgress) && encodedProgress >= 0 ? encodedProgress : null);
    const dataLabel = this.stringifyTaskBarLabel(task?.dataLabel || api.value(4));
    const children: any[] = [];

    if (useProgressTrack && progress !== null) {
      children.push({
        type: 'rect',
        shape: { ...rect, r: 8 },
        style: {
          fill: this.withAlpha(baseColor, 0.2),
          stroke: this.withAlpha(baseColor, 0.35),
          lineWidth: 1,
          opacity,
        },
      });

      if (progress > 0) {
        children.push({
          type: 'rect',
          shape: {
            x: rect.x,
            y: rect.y,
            width: Math.max(6, rect.width * (progress / 100)),
            height: rect.height,
            r: 8,
          },
          style: {
            fill: baseColor,
            stroke: barStroke,
            lineWidth: 1,
            opacity,
          },
        });
      }
    } else {
      const solidBarStyle = {
        ...seriesStyle,
        fill: baseColor,
        stroke: barStroke,
        lineWidth: 1,
        opacity,
      };
      children.push({
        type: 'rect',
        shape: { ...rect, r: 8 },
        style: solidBarStyle,
      });
    }

    if (dataLabel ) {
      const dataLabelFontSize = this.resolveDataLabelFontSize();
      const labelX = useProgressTrack && progress !== null && progress < 45
        ? rect.x + Math.min(rect.width - 12, Math.max(8, rect.width * (progress / 100) + 8))
        : rect.x + 8;
      const labelWidth = Math.max(20, rect.width - (labelX - rect.x) - 8);
      const textColor = useProgressTrack && progress !== null && progress < 45
        ? lowProgressTextColor
        : getContrastingTextColor(baseColor);
      children.push({
        type: 'text',
        style: {
          x: labelX,
          y: rect.y + rect.height / 2,
          text: dataLabel,
          textAlign: 'left',
          textVerticalAlign: 'middle',
          fill: textColor,
          opacity,
          width: labelWidth,
          overflow: 'truncate',
          font: `600 ${dataLabelFontSize}px sans-serif`,
        },
      });
    } else if (showProgressLabel && progress !== null && rect.width > 72) {
      const textColor = progress >= 45 ? getContrastingTextColor(baseColor) : lowProgressTextColor;
      children.push({
        type: 'text',
        style: {
          x: rect.x + 8,
          y: rect.y + rect.height / 2,
          text: `${progress}%`,
          textAlign: 'left',
          textVerticalAlign: 'middle',
          fill: textColor,
          opacity,
          width: rect.width - 16,
          overflow: 'truncate',
          font: `600 ${progressLabelFontSize}px sans-serif`,
        },
      });
    }

    return { type: 'group', children };
  }

  protected buildTimelineAxes(
    mode: TimelineMode,
    domain: { min: number; max: number },
    axisPosition: 'top' | 'bottom',
    upperMode: TimelineMode | null,
  ): echarts.EChartsOption['xAxis'] {
    const lowerOffset = upperMode ? 24 : 0;
    const lowerAxis = this.createTimelineAxis(mode, domain, axisPosition, lowerOffset, true, false);

    if (!upperMode) {
      return lowerAxis;
    }

    const upperAxis = this.createTimelineAxis(upperMode, domain, axisPosition, 0, false, true);
    return axisPosition === 'top' ? [upperAxis, lowerAxis] : [lowerAxis, upperAxis];
  }

  protected createTimelineAxis(
    mode: TimelineMode,
    domain: { min: number; max: number },
    position: 'top' | 'bottom',
    offset: number,
    showSplitLine: boolean,
    emphasizeLabels: boolean,
  ): any {
    const chartWidth = this.getChartWidth();
    const compactLabel = !emphasizeLabels || chartWidth < 640;
    const labelFontSize = this.resolveTimelineLabelFontSize(emphasizeLabels);
    const splitNumber = this.resolveTimelineSplitNumber(mode, emphasizeLabels, chartWidth);
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const axisLineColor = theme.chart?.axisLineColor || '#cbd5e1';
    const emphasizedLabelColor = theme.chart?.textColor || '#0f172a';
    const mutedLabelColor = theme.chart?.axisLabelColor || '#475569';
    const splitLineColor = theme.chart?.splitLineColor || '#e2e8f0';
    return {
      type: 'time',
      min: domain.min,
      max: domain.max,
      minInterval: this.minInterval(mode),
      splitNumber,
      position,
      offset,
      axisLine: { lineStyle: { color: axisLineColor } },
      axisTick: {
        show: true,
        length: emphasizeLabels ? 8 : 5,
        lineStyle: { color: axisLineColor },
      },
      axisLabel: {
        color: emphasizeLabels ? emphasizedLabelColor : mutedLabelColor,
        hideOverlap: true,
        margin: emphasizeLabels ? 10 : 12,
        fontWeight: emphasizeLabels ? 600 : 500,
        fontSize: labelFontSize,
        lineHeight: Math.round(labelFontSize * 1.25),
        padding: [0, 4, 0, 4],
        formatter: (value: number) => this.tick(value, mode, compactLabel),
      },
      splitLine: showSplitLine ? { show: true, lineStyle: { color: splitLineColor } } : { show: false },
    };
  }

  protected resolveTimelineMode(): TimelineMode {
    return this.normalizeTimelineMode(this.data?.chartConfig?.timelineMode || this.data?.timelineMode || 'monthly');
  }

  protected resolveUpperTimelineMode(lowerMode: TimelineMode): TimelineMode | null {
    const configured = this.data?.chartConfig?.upperTimelineMode || this.data?.upperTimelineMode;
    const normalizedConfigured = String(configured || '').trim().toLowerCase();

    if (normalizedConfigured === 'none' || normalizedConfigured === 'off' || normalizedConfigured === 'false') {
      return null;
    }

    if (normalizedConfigured) {
      return this.normalizeTimelineMode(configured);
    }

    if (lowerMode === 'monthly' || lowerMode === 'quarterly') return 'yearly';
    if (lowerMode === 'weekly' || lowerMode === 'date') return 'monthly';
    return null;
  }

  protected normalizeTimelineMode(value: unknown): TimelineMode {
    const text = String(value || '').toLowerCase();
    if (text === 'day' || text === 'daily' || text === 'date') return 'date';
    if (text === 'week' || text === 'weekly') return 'weekly';
    if (text === 'month' || text === 'monthly') return 'monthly';
    if (text === 'quarter' || text === 'quarterly' || text === 'qtr') return 'quarterly';
    if (text === 'year' || text === 'yearly') return 'yearly';
    return 'date';
  }

  protected resolveDomain(tasks: TaskItem[], mode: TimelineMode): { min: number; max: number } {
    const configuredMin = this.parseDate(this.data?.chartConfig?.startDate || this.data?.startDate, false);
    const configuredMax = this.parseDate(this.data?.chartConfig?.endDate || this.data?.endDate, true);
    let min = configuredMin ?? Math.min(...tasks.map((task) => task.start));
    let max = configuredMax ?? Math.max(...tasks.map((task) => task.end));

    if (configuredMin === null || configuredMin === undefined) {
      min = mode === 'yearly'
        ? this.startOfYear(min)
        : mode === 'quarterly'
          ? this.startOfQuarter(min)
        : mode === 'monthly'
          ? this.startOfMonth(min)
          : mode === 'weekly'
            ? this.startOfWeek(min)
            : this.startOfDay(min - DAY_MS);
    }
    if (configuredMax === null || configuredMax === undefined) {
      max = mode === 'yearly'
        ? this.endOfYear(max)
        : mode === 'quarterly'
          ? this.endOfQuarter(max)
        : mode === 'monthly'
          ? this.endOfMonth(max)
          : mode === 'weekly'
            ? this.endOfWeek(max)
            : this.endOfDay(max + DAY_MS);
    }
    return max > min ? { min, max } : { min, max: min + 7 * DAY_MS };
  }

  protected resolveHeight(count: number): string {
    if (this.isInFullscreenFocus()) {
      return '100%';
    }

    const configuredHeight = this.data?.height ?? this.data?.chartConfig?.height;
    if (typeof configuredHeight === 'number') return `${configuredHeight}px`;
    if (typeof configuredHeight === 'string' && configuredHeight.trim()) {
      const normalized = configuredHeight.trim().toLowerCase();
      // Grid already applies relative widget height. Using the same relative
      // units inside chart host can cause overflow or shrink behavior.
      if (normalized.endsWith('vh') || normalized.endsWith('vw') || normalized.endsWith('%')) {
        return '100%';
      }
      return configuredHeight;
    }
    const rowHeight = this.useWrappedTaskLabels() ? 54 : 46;
    const taskBased = Math.max(320, count * rowHeight + 96);
    // Also respect the responsive CSS variable so on large/TV screens
    // the chart fills more vertical space rather than collapsing to task count.
    const cssVarPx = this.readCssVarPx('--dvm-chart-height-lg');
    return `${Math.max(taskBased, cssVarPx)}px`;
  }

  protected isInFullscreenFocus(): boolean {
    if (typeof document === 'undefined' || !this.chartHost?.nativeElement) {
      return false;
    }

    const fullscreenEl = document.fullscreenElement as HTMLElement | null;
    if (!fullscreenEl) {
      return false;
    }

    return fullscreenEl.contains(this.chartHost.nativeElement);
  }

  protected readCssVarPx(name: string): number {
    try {
      const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      if (!raw) return 0;
      const m = raw.match(/^([\d.]+)px$/);
      if (m) return parseFloat(m[1]);
      return 0;
    } catch {
      return 0;
    }
  }

  protected resolveTimelineGridPadding(position: 'top' | 'bottom', hasUpperAxis: boolean): number {
    const chartWidth = this.getChartWidth();
    const compactWidth = chartWidth < 640;

    if (position === 'top') {
      if (hasUpperAxis) {
        return compactWidth ? 70 : 58;
      }
      return compactWidth ? 42 : 34;
    }

    if (hasUpperAxis) {
      return compactWidth ? 72 : 58;
    }
    return compactWidth ? 52 : 40;
  }

  protected resolveCardHeight(chartHeight: string): string {
    const chartHeightPx = this.toPixels(chartHeight);
    if (chartHeightPx === null) {
      return chartHeight;
    }

    const baseChrome = 118;
    const legendRows = this.legendItems.length ? Math.ceil(this.legendItems.length / 4) : 0;
    const legendHeight = legendRows > 0 ? legendRows * 30 + 12 : 0;
    return `${chartHeightPx + baseChrome + legendHeight}px`;
  }

  protected resolveLeft(tasks: TaskItem[]): number {
    const chartWidth = this.getChartWidth();
    const maxLabelWidth = chartWidth < 420
      ? Math.max(112, Math.floor(chartWidth * 0.34))
      : chartWidth < 768
        ? Math.max(140, Math.floor(chartWidth * 0.38))
        : Math.max(170, Math.floor(chartWidth * 0.42));
    const configured = Number(this.data?.chartConfig?.taskLabelWidth);
    if (!Number.isNaN(configured) && configured > 0) return Math.min(configured, maxLabelWidth);
    const longest = tasks.reduce((max, task) => Math.max(max, task.rowLabel.length), 18);
    return Math.min(maxLabelWidth, Math.max(140, longest * 7 + 36));
  }

  protected resolveFontScale(): number {
    if (typeof document === 'undefined') {
      return 1;
    }

    const configured = Number(this.data?.chartConfig?.fontScale);
    if (Number.isFinite(configured) && configured > 0) {
      return configured;
    }

    const cssValue = Number(
      getComputedStyle(document.documentElement).getPropertyValue('--dvm-font-scale').trim(),
    );
    return Number.isFinite(cssValue) && cssValue > 0 ? cssValue : 1;
  }

  protected resolveFontSize(value: unknown, fallback: number): number {
    const configured = Number(value);
    const baseSize = Number.isFinite(configured) && configured > 0 ? configured : fallback;
    return Math.max(10, Math.round(baseSize * this.resolveFontScale()));
  }

  protected resolveTaskLabelFontSize(): number {
    const baseSize = this.resolveFontSize(this.data?.chartConfig?.taskLabelFontSize, 13);
    return this.getChartWidth() < 640 ? Math.max(11, baseSize - 1) : baseSize;
  }

  protected resolveTimelineLabelFontSize(emphasizeLabels: boolean): number {
    const configured = emphasizeLabels
      ? this.data?.chartConfig?.timelineLabelFontSize
      : this.data?.chartConfig?.upperTimelineLabelFontSize ?? this.data?.chartConfig?.timelineLabelFontSize;
    const baseSize = this.resolveFontSize(configured, emphasizeLabels ? 12 : 11);
    return this.getChartWidth() < 640 ? Math.max(10, baseSize - 1) : baseSize;
  }

  protected resolveProgressLabelFontSize(): number {
    return this.resolveFontSize(this.data?.chartConfig?.progressLabelFontSize, 12);
  }

  protected resolveDataLabelFontSize(): number {
    return this.resolveFontSize(
      this.data?.chartConfig?.dataLabelFontSize ?? this.data?.chartConfig?.progressLabelFontSize,
      11,
    );
  }

  protected resolveColorKey(
    row: TaskRecord,
    colorBy: string,
    context: { name: string; group: string; status: string; owner: string },
  ): string {
    const normalized = colorBy.trim().toLowerCase();

    if (this.matchesConfiguredKey(colorBy, this.data?.taskNameKey) || ['name', 'task', 'label', 'title'].includes(normalized)) {
      return context.name || context.status || context.group || context.owner || 'Unassigned';
    }

    if (this.matchesConfiguredKey(colorBy, this.data?.groupKey) || ['group', 'track', 'stream', 'category'].includes(normalized)) {
      return context.group || context.status || context.name || context.owner || 'Unassigned';
    }

    if (this.matchesConfiguredKey(colorBy, this.data?.statusKey) || ['status', 'phase', 'state'].includes(normalized)) {
      return context.status || context.group || context.name || context.owner || 'Unassigned';
    }

    if (this.matchesConfiguredKey(colorBy, this.data?.ownerKey) || ['owner', 'team', 'assignee'].includes(normalized)) {
      return context.owner || context.group || context.status || context.name || 'Unassigned';
    }

    const directValue = this.readString(row, colorBy, [colorBy]);
    return directValue || context.status || context.group || context.name || context.owner || 'Unassigned';
  }

  protected resolveColorMapValue(colorMap: Record<string, string>, colorKey: string): string | null {
    if (!colorKey) {
      return null;
    }

    if (colorMap[colorKey]) {
      return colorMap[colorKey];
    }

    const normalizedKey = colorKey.trim().toLowerCase();
    const matchedKey = Object.keys(colorMap).find((key) => key.trim().toLowerCase() === normalizedKey);
    return matchedKey ? colorMap[matchedKey] : null;
  }

  protected matchesConfiguredKey(colorBy: string, configuredKey: unknown): boolean {
    return typeof configuredKey === 'string'
      && configuredKey.trim().toLowerCase() === colorBy.trim().toLowerCase();
  }

  protected useWrappedTaskLabels(): boolean {
    return this.data?.chartConfig?.wrapTaskLabels !== false;
  }

  protected formatTaskAxisLabel(value: string, maxWidth: number, fontSize: number): string {
    const label = String(value || '').trim();
    if (!label || !this.useWrappedTaskLabels()) {
      return label;
    }

    const maxCharsPerLine = Math.max(10, Math.floor(maxWidth / Math.max(fontSize * 0.62, 6)));
    const separator = String(this.data?.chartConfig?.labelSeparator || ' • ');

    if (label.includes(separator)) {
      const [group, ...rest] = label.split(separator);
      const firstLine = this.truncateText(group.trim(), maxCharsPerLine);
      const secondLine = this.truncateText(rest.join(separator).trim(), maxCharsPerLine);
      return secondLine ? `${firstLine}\n${secondLine}` : firstLine;
    }

    return this.wrapText(label, maxCharsPerLine, 2);
  }

  protected resolveResponsiveUpperTimelineMode(upperMode: TimelineMode | null): TimelineMode | null {
    if (!upperMode) {
      return null;
    }

    if (this.data?.chartConfig?.alwaysShowUpperTimeline === true) {
      return upperMode;
    }

    const hideBelow = Number(this.data?.chartConfig?.hideUpperTimelineBelow || 640);
    return this.getChartWidth() < hideBelow ? null : upperMode;
  }

  protected resolveTimelineSplitNumber(
    mode: TimelineMode,
    emphasizeLabels: boolean,
    chartWidth: number,
  ): number | undefined {
    if (mode === 'yearly') {
      return chartWidth < 640 ? 4 : undefined;
    }

    if (chartWidth < 420) {
      return emphasizeLabels ? 3 : 4;
    }

    if (chartWidth < 640) {
      return emphasizeLabels ? 4 : 5;
    }

    if (chartWidth < 900) {
      return emphasizeLabels ? 5 : 7;
    }

    return undefined;
  }

  protected getTimelineLabel(mode: TimelineMode): string {
    return mode === 'weekly'
      ? 'Weekly Timeline'
      : mode === 'monthly'
        ? 'Monthly Timeline'
        : mode === 'quarterly'
          ? 'Quarterly Timeline'
        : mode === 'yearly'
          ? 'Yearly Timeline'
          : 'Date Timeline';
  }

  protected tick(value: number, mode: TimelineMode, compact = false): string {
    const date = new Date(value);
    if (mode === 'yearly') {
      return new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(date);
    }
    if (mode === 'quarterly') {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return compact ? `Q${quarter}` : `Q${quarter} ${date.getFullYear()}`;
    }
    if (mode === 'monthly') {
      return new Intl.DateTimeFormat('en-US', compact ? { month: 'short' } : { month: 'short', year: 'numeric' }).format(date);
    }
    if (mode === 'weekly') {
      const week = this.isoWeek(date);
      return compact ? `W${week.week}` : `W${week.week} ${week.year}`;
    }
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
  }

  protected minInterval(mode: TimelineMode): number {
    const step = Math.max(1, Number(this.data?.chartConfig?.timelineStep || 1));
    return mode === 'yearly'
      ? 365 * DAY_MS * step
      : mode === 'quarterly'
        ? 90 * DAY_MS * step
      : mode === 'monthly'
        ? 28 * DAY_MS * step
        : mode === 'weekly'
          ? 7 * DAY_MS * step
          : DAY_MS * step;
  }

  protected buildRowLabel(name: string, group: string): string {
    const labelMode = String(this.data?.chartConfig?.rowLabelMode || 'group-task').toLowerCase();
    const separator = String(this.data?.chartConfig?.labelSeparator || ' • ');

    if (labelMode === 'task' || labelMode === 'task-only') {
      return name;
    }
    if (labelMode === 'group') {
      return group || name;
    }
    return group ? `${group}${separator}${name}` : name;
  }

  protected buildSelectionKey(name: string, group: string): string {
    const selectionMode = String(
      this.data?.chartConfig?.selectionKeyMode || this.data?.chartConfig?.rowLabelMode || 'group-task',
    ).toLowerCase();
    const separator = String(this.data?.chartConfig?.labelSeparator || ' • ');

    if (selectionMode === 'task' || selectionMode === 'task-only') {
      return name;
    }
    if (selectionMode === 'group') {
      return group || name;
    }
    return group ? `${group}${separator}${name}` : name;
  }

  protected resolveTaskBarLabel(
    row: TaskRecord,
    context: { name: string; group: string; status: string; owner: string; progress: number | null },
  ): string {
    const configuredKey = String(this.data?.chartConfig?.dataLabelKey || this.data?.dataLabelKey || '').trim();
    if (!configuredKey) {
      return '';
    }

    const normalized = configuredKey.toLowerCase();
    if (this.matchesConfiguredKey(configuredKey, this.data?.taskNameKey) || ['name', 'task', 'label', 'title'].includes(normalized)) {
      return context.name;
    }

    if (this.matchesConfiguredKey(configuredKey, this.data?.groupKey) || ['group', 'track', 'stream', 'category'].includes(normalized)) {
      return context.group;
    }

    if (this.matchesConfiguredKey(configuredKey, this.data?.statusKey) || ['status', 'phase', 'state'].includes(normalized)) {
      return context.status;
    }

    if (this.matchesConfiguredKey(configuredKey, this.data?.ownerKey) || ['owner', 'team', 'assignee'].includes(normalized)) {
      return context.owner;
    }

    if (this.matchesConfiguredKey(configuredKey, this.data?.progressKey) || ['progress', 'completion'].includes(normalized)) {
      return context.progress === null ? '' : `${context.progress}%`;
    }

    return this.stringifyTaskBarLabel(this.readValue(row, configuredKey, [configuredKey]));
  }

  protected uniquify(values: string[]): string[] {
    const counts = new Map<string, number>();
    const seen = new Map<string, number>();

    values.forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));

    return values.map((value) => {
      if ((counts.get(value) || 0) === 1) {
        return value;
      }

      const next = (seen.get(value) || 0) + 1;
      seen.set(value, next);
      return `${value} (${next})`;
    });
  }

  protected resolveLegendItems(tasks: TaskItem[]): LegendItem[] {
    if (this.data?.chartConfig?.showLegend === false) {
      return [];
    }

    const items = new Map<string, LegendItem>();
    for (const task of tasks) {
      const label = task.colorKey || task.status || task.group || task.name;
      if (!items.has(label)) {
        items.set(label, { label, color: task.color });
      }
    }

    return Array.from(items.values());
  }

  protected onLegendClick(label: string): void {
    const isMultiSelect = this.data?.isMultiSelect !== false;

    if (isMultiSelect) {
      this.selectedLegendLabels = this.selectedLegendLabels.includes(label)
        ? this.selectedLegendLabels.filter((item) => item !== label)
        : [...this.selectedLegendLabels, label];
    } else {
      this.selectedLegendLabels =
        this.selectedLegendLabels.length === 1 && this.selectedLegendLabels[0] === label
          ? []
          : [label];
    }

    this.renderVisibleTasks();
  }

  protected isLegendActive(label: string): boolean {
    return this.selectedLegendLabels.length === 0 || this.selectedLegendLabels.includes(label);
  }

  protected renderVisibleTasks(): void {
    const visibleTasks = this.selectedLegendLabels.length
      ? this.allTasks.filter((task) => this.selectedLegendLabels.includes(task.colorKey))
      : this.allTasks;

    if (!visibleTasks.length || !this.currentDomain) {
      this.taskCount = 0;
      this.allCategories = [];
      this.visibleTasks = [];
      this.myChart?.clear();
      this.myChart?.hideLoading();
      return;
    }

    this.taskCount = visibleTasks.length;
    this.visibleTasks = visibleTasks;
    this.allCategories = visibleTasks.map((task) => task.selectionKey);
    this.resolvedHeight = this.resolveHeight(visibleTasks.length);
    this.resolvedCardHeight = this.resolveCardHeight(this.resolvedHeight);
    this.chartHost.nativeElement.style.height = this.resolvedHeight;

    this.myChart.setOption(this.buildOption(visibleTasks, this.currentMode, this.currentDomain), true);
    this.myChart.hideLoading();
    this.wrapper.afterRender();
    setTimeout(() => this.myChart?.resize(), 0);
  }

  protected refreshResponsiveLayout(): void {
    if (!this.myChart) {
      return;
    }

    if (this.allTasks.length && this.currentDomain) {
      this.renderVisibleTasks();
      return;
    }

    this.myChart.resize();
  }

  protected applyCategoryHighlight(categories: string[]): void {
    if (!this.myChart) {
      return;
    }

    const option = this.myChart.getOption() as any;
    if (!option?.series?.length) {
      return;
    }

    const categoryIndices = categories
      .map((category) => this.allCategories.indexOf(category))
      .filter((index) => index !== -1);

    const updatedSeries = option.series.map((series: any) => ({
      ...series,
      data: series.data?.map((item: any, index: number) => {
        const opacity = categoryIndices.length === 0 || categoryIndices.includes(index) ? 1 : 0.24;
        return {
          ...item,
          itemStyle: {
            ...(item?.itemStyle || {}),
            opacity,
          },
        };
      }),
    }));

    this.myChart.setOption({ series: updatedSeries });
  }

  protected resetCategoryHighlight(): void {
    if (!this.myChart) {
      return;
    }

    const option = this.myChart.getOption() as any;
    if (!option?.series?.length) {
      return;
    }

    const updatedSeries = option.series.map((series: any) => ({
      ...series,
      data: series.data?.map((item: any) => {
        const itemStyle = { ...(item?.itemStyle || {}) };
        delete itemStyle.opacity;
        return {
          ...item,
          itemStyle,
        };
      }),
    }));

    this.myChart.setOption({ series: updatedSeries });
  }

  protected readValue(row: TaskRecord, configured: string | undefined, fallbacks: string[]): unknown {
    const keys = [configured, ...fallbacks].filter((key): key is string => !!key);
    for (const key of keys) {
      const value = this.byPath(row, key);
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return undefined;
  }

  protected readString(row: TaskRecord, configured: string | undefined, fallbacks: string[]): string {
    const value = this.readValue(row, configured, fallbacks);
    return value === undefined || value === null ? '' : String(value).trim();
  }

  protected stringifyTaskBarLabel(value: unknown): string {
    if (value === undefined || value === null) {
      return '';
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? String(value) : '';
    }

    return String(value).trim();
  }

  protected parseProgress(value: unknown): number | null {
    if (value === undefined || value === null || value === '') return null;
    const numeric = Number(String(value).replace('%', '').trim());
    return Number.isFinite(numeric) ? Math.max(0, Math.min(100, Math.round(numeric))) : null;
  }

  protected parseDate(value: unknown, treatAsEnd: boolean): number | null {
    if (value === undefined || value === null || value === '') return null;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number' && Number.isFinite(value)) return Math.abs(value) < 1e11 ? value * 1000 : value;
    if (typeof value !== 'string') return null;
    const text = value.trim();
    if (/^\d{13}$/.test(text)) return Number(text);
    if (/^\d{10}$/.test(text)) return Number(text) * 1000;
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      const parsed = Date.parse(`${text}T00:00:00`);
      return Number.isNaN(parsed) ? null : treatAsEnd ? this.endOfDay(parsed) : this.startOfDay(parsed);
    }
    const parsed = Date.parse(text);
    return Number.isNaN(parsed) ? null : parsed;
  }

  protected formatDuration(start: number, end: number): string {
    const totalDays = Math.max(1, Math.round((this.endOfDay(end) - this.startOfDay(start)) / DAY_MS) + 1);

    if (totalDays >= 365) {
      const years = totalDays / 365;
      return `${years.toFixed(years >= 2 ? 0 : 1)} years`;
    }
    if (totalDays >= 90) {
      const quarters = totalDays / 91;
      return `${quarters.toFixed(1)} quarters`;
    }
    if (totalDays >= 30) {
      const months = totalDays / 30;
      return `${months.toFixed(1)} months`;
    }
    if (totalDays >= 7) {
      const weeks = totalDays / 7;
      return `${weeks.toFixed(1)} weeks`;
    }
    return `${totalDays} days`;
  }

  protected byPath(source: any, path: string): any {
    return path.split('.').reduce((current: any, key: string) => (current == null ? undefined : /^\d+$/.test(key) ? current[Number(key)] : current[key]), source);
  }

  protected formatDate(value: number): string {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(value));
  }

  protected startOfDay(value: number): number {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  }

  protected endOfDay(value: number): number {
    const date = new Date(value);
    date.setHours(23, 59, 59, 999);
    return date.getTime();
  }

  protected startOfWeek(value: number): number {
    const date = new Date(this.startOfDay(value));
    const diff = date.getDay() === 0 ? -6 : 1 - date.getDay();
    date.setDate(date.getDate() + diff);
    return this.startOfDay(date.getTime());
  }

  protected endOfWeek(value: number): number {
    return this.endOfDay(this.startOfWeek(value) + 6 * DAY_MS);
  }

  protected startOfMonth(value: number): number {
    const date = new Date(value);
    return new Date(date.getFullYear(), date.getMonth(), 1).getTime();
  }

  protected endOfMonth(value: number): number {
    const date = new Date(value);
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
  }

  protected startOfQuarter(value: number): number {
    const date = new Date(value);
    const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
    return new Date(date.getFullYear(), quarterStartMonth, 1).getTime();
  }

  protected endOfQuarter(value: number): number {
    const date = new Date(value);
    const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
    return new Date(date.getFullYear(), quarterStartMonth + 3, 0, 23, 59, 59, 999).getTime();
  }

  protected startOfYear(value: number): number {
    const date = new Date(value);
    return new Date(date.getFullYear(), 0, 1).getTime();
  }

  protected endOfYear(value: number): number {
    const date = new Date(value);
    return new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999).getTime();
  }

  protected isoWeek(date: Date): { week: number; year: number } {
    const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = utc.getUTCDay() || 7;
    utc.setUTCDate(utc.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((utc.getTime() - yearStart.getTime()) / DAY_MS) + 1) / 7);
    return { week, year: utc.getUTCFullYear() };
  }

  protected withAlpha(color: string, alpha: number): string {
    const normalized = color.trim();

    if (/^#([0-9a-f]{3}){1,2}$/i.test(normalized)) {
      let hex = normalized.slice(1);
      if (hex.length === 3) {
        hex = hex.split('').map((char) => char + char).join('');
      }

      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    const rgbMatch = normalized.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbMatch) {
      const parts = rgbMatch[1].split(',').map((part) => part.trim());
      const [r = '0', g = '0', b = '0'] = parts;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    return normalized;
  }

  protected wrapText(text: string, maxCharsPerLine: number, maxLines: number): string {
    if (text.length <= maxCharsPerLine) {
      return text;
    }

    const words = text.split(/\s+/).filter(Boolean);
    if (!words.length) {
      return this.truncateText(text, maxCharsPerLine);
    }

    const lines: string[] = [];
    let currentLine = '';

    for (let index = 0; index < words.length; index += 1) {
      const word = words[index];
      const candidate = currentLine ? `${currentLine} ${word}` : word;

      if (candidate.length <= maxCharsPerLine) {
        currentLine = candidate;
        continue;
      }

      if (!currentLine) {
        lines.push(this.truncateText(word, maxCharsPerLine));
      } else {
        lines.push(currentLine);
        currentLine = word;
      }

      if (lines.length === maxLines - 1) {
        const remaining = [currentLine, ...words.slice(index + 1)].filter(Boolean).join(' ');
        lines.push(this.truncateText(remaining, maxCharsPerLine));
        return lines.join('\n');
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.slice(0, maxLines).join('\n');
  }

  protected truncateText(text: string, maxChars: number): string {
    const normalized = text.trim();
    if (normalized.length <= maxChars) {
      return normalized;
    }

    if (maxChars <= 1) {
      return '…';
    }

    return `${normalized.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
  }

  protected toPixels(value: string): number | null {
    const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/i);
    return match ? Number(match[1]) : null;
  }

  protected getChartWidth(): number {
    return this.myChart?.getWidth()
      || this.chartHost?.nativeElement?.clientWidth
      || (typeof window !== 'undefined' ? window.innerWidth : 1024);
  }
}
